'use client';

import { createContext, useContext, useCallback, useState } from 'react';
import { Toast } from '@/components/ui/toast';
import { useToast as useToastHook } from '@/hooks/use-toast';

type ToastType = 'default' | 'destructive' | 'success';

type ToastContextType = {
    toast: (props: { title: string; description: string; variant?: ToastType }) => void;
};

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export function ToastProvider({ children }: { children: React.ReactNode }) {
    const [toasts, setToasts] = useState<Array<{
        id: string;
        title: string;
        description: string;
        type: ToastType;
    }>>([]);

    const toast = useCallback(
        ({ title, description, variant = 'default' }: { title: string; description: string; variant?: ToastType }) => {
            const id = Math.random().toString(36).substring(2, 9);
            setToasts((prev) => [...prev, { id, title, description, type: variant }]);
        },
        []
    );

    const removeToast = useCallback((id: string) => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
    }, []);

    return (
        <ToastContext.Provider value={{ toast }}>
            {children}
            <div className="fixed bottom-0 right-0 z-50 flex flex-col space-y-2 p-4">
                {toasts.map((toast) => (
                    <Toast
                        key={toast.id}
                        id={toast.id}
                        title={toast.title}
                        description={toast.description}
                        type={toast.type}
                        onDismiss={removeToast}
                    />
                ))}
            </div>
        </ToastContext.Provider>
    );
}

export function useToast() {
    const context = useContext(ToastContext);
    if (context === undefined) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
}
